import { supabase, withAuthRetry } from './supabase';
import { logger } from './logger';
import { sendAdminNotification } from './notificationService';
import { disableAccount } from './userService';
import { rejectVideo, webPlayableUrl } from './videoService';
import { closeOfferAsAdmin } from './offerService';

export type ReportTargetType = 'profile' | 'content' | 'message' | 'offer';
export type ReportStatus = 'pending' | 'reviewed' | 'resolved' | 'dismissed';

export interface ReportPerson {
  userId: string;
  role: string;
  name: string;
  profileRowId: string | null;
}

export interface ReportRow {
  id: string;
  reporter: ReportPerson;
  reported: ReportPerson;
  targetType: ReportTargetType;
  targetId: string | null;
  conversationId: string | null;
  reason: string;
  details: string;
  status: ReportStatus;
  adminNotes: string;
  createdAt: string;
  createdAtIso: string;
  reviewedAt: string | null;
}

export interface ReportAction {
  id: string;
  action: string;
  payload: Record<string, unknown>;
  adminId: string;
  createdAt: string;
}

export interface ContentPreview {
  source: 'candidate' | 'employer';
  url: string | null;
  thumbnailUrl: string | null;
  mediaType: 'video' | 'image';
  status: string;
  textOverlay: string | null;
}

export interface OfferPreview {
  id: string;
  title: string;
  company: string;
  city: string;
  status: string;
}

export interface ThreadMessage {
  id: string;
  senderId: string;
  body: string;
  createdAt: string;
}

const REASON_LABELS: Record<string, string> = {
  harassment: 'Harcèlement',
  fake_profile: 'Faux profil',
  inappropriate: 'Contenu inapproprié',
  spam: 'Spam',
  scam: 'Arnaque',
  other: 'Autre',
};

const TARGET_LABELS: Record<ReportTargetType, string> = {
  profile: 'Profil',
  content: 'Contenu',
  message: 'Message',
  offer: 'Offre',
};

export function reasonLabel(reason: string): string {
  return REASON_LABELS[reason] || reason;
}

export function targetLabel(type: ReportTargetType): string {
  return TARGET_LABELS[type] || type;
}

function emptyPerson(userId: string): ReportPerson {
  return { userId, role: '', name: userId.slice(0, 8), profileRowId: null };
}

async function resolvePersons(userIds: string[]): Promise<Record<string, ReportPerson>> {
  const unique = [...new Set(userIds.filter(Boolean))];
  const map: Record<string, ReportPerson> = {};
  if (unique.length === 0) return map;

  const [profilesRes, candidatesRes, employersRes] = await Promise.all([
    supabase.from('profiles').select('id, role').in('id', unique),
    supabase.from('candidate_profiles').select('id, user_id, first_name, last_name').in('user_id', unique),
    supabase.from('employer_profiles').select('id, user_id, company_name, first_name, last_name').in('user_id', unique),
  ]);

  for (const id of unique) map[id] = emptyPerson(id);

  for (const p of profilesRes.data || []) {
    map[p.id] = { ...map[p.id], userId: p.id, role: p.role || '' };
  }
  for (const c of candidatesRes.data || []) {
    const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
    map[c.user_id] = {
      ...map[c.user_id],
      name: name || map[c.user_id].name,
      profileRowId: c.id,
      role: map[c.user_id].role || 'candidate',
    };
  }
  for (const e of employersRes.data || []) {
    const name = e.company_name || `${e.first_name || ''} ${e.last_name || ''}`.trim();
    map[e.user_id] = {
      ...map[e.user_id],
      name: name || map[e.user_id].name,
      profileRowId: e.id,
      role: map[e.user_id].role || 'employer',
    };
  }
  return map;
}

function mapRow(r: any, people: Record<string, ReportPerson>): ReportRow {
  return {
    id: r.id,
    reporter: people[r.reporter_id] || emptyPerson(r.reporter_id),
    reported: people[r.reported_id] || emptyPerson(r.reported_id),
    targetType: r.target_type,
    targetId: r.target_id || null,
    conversationId: r.conversation_id || null,
    reason: r.reason || '',
    details: r.details || '',
    status: r.status || 'pending',
    adminNotes: r.admin_notes || '',
    createdAt: new Date(r.created_at).toLocaleString('fr-FR'),
    createdAtIso: r.created_at,
    reviewedAt: r.reviewed_at ? new Date(r.reviewed_at).toLocaleString('fr-FR') : null,
  };
}

export async function fetchReports(): Promise<ReportRow[]> {
  const { data, error } = await withAuthRetry(async () =>
    await supabase.from('reports').select('*').order('created_at', { ascending: false }),
  );
  if (error) {
    logger.error('fetch_reports', 'reportService', { error: error.message });
    throw error;
  }
  const people = await resolvePersons(
    (data || []).flatMap((r: any) => [r.reporter_id, r.reported_id]),
  );
  logger.technical('fetch_reports_ok', 'reportService', { count: (data || []).length });
  return (data || []).map((r: any) => mapRow(r, people));
}

async function currentAdminId(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user.id) throw new Error('Not authenticated');
  return session.user.id;
}

export async function insertReportAction(
  reportId: string,
  action: string,
  payload: Record<string, unknown> = {},
): Promise<void> {
  const adminId = await currentAdminId();
  const { error } = await supabase.from('report_actions').insert({
    report_id: reportId,
    admin_id: adminId,
    action,
    payload,
  });
  if (error) {
    logger.error('insert_report_action', 'reportService', { reportId, action, error: error.message });
    throw error;
  }
}

export async function fetchReportActions(reportId: string): Promise<ReportAction[]> {
  const { data, error } = await supabase
    .from('report_actions')
    .select('*')
    .eq('report_id', reportId)
    .order('created_at', { ascending: false });
  if (error) {
    logger.error('fetch_report_actions', 'reportService', { reportId, error: error.message });
    throw error;
  }
  return (data || []).map((a: any) => ({
    id: a.id,
    action: a.action,
    payload: a.payload || {},
    adminId: a.admin_id,
    createdAt: new Date(a.created_at).toLocaleString('fr-FR'),
  }));
}

export async function fetchContentPreview(targetId: string): Promise<ContentPreview | null> {
  const candidate = await supabase
    .from('candidate_videos')
    .select('video_url, thumbnail_url, media_type, status, text_overlay')
    .eq('id', targetId)
    .maybeSingle();
  if (candidate.data) {
    const url = candidate.data.video_url;
    return {
      source: 'candidate',
      url: url ? webPlayableUrl(url) : null,
      thumbnailUrl: candidate.data.thumbnail_url,
      mediaType: candidate.data.media_type === 'image' ? 'image' : 'video',
      status: candidate.data.status,
      textOverlay: candidate.data.text_overlay,
    };
  }
  const employer = await supabase
    .from('employer_videos')
    .select('video_url, thumbnail_url, media_type, status, text_overlay')
    .eq('id', targetId)
    .maybeSingle();
  if (employer.data) {
    const url = employer.data.video_url;
    return {
      source: 'employer',
      url: url ? webPlayableUrl(url) : null,
      thumbnailUrl: employer.data.thumbnail_url,
      mediaType: employer.data.media_type === 'image' ? 'image' : 'video',
      status: employer.data.status,
      textOverlay: employer.data.text_overlay,
    };
  }
  return null;
}

export async function fetchOfferPreview(offerId: string): Promise<OfferPreview | null> {
  const { data } = await supabase
    .from('job_offers')
    .select('id, position_name, establishment_city, status, employer_profiles!job_offers_employer_id_fkey ( company_name )')
    .eq('id', offerId)
    .maybeSingle();
  if (!data) return null;
  const company = Array.isArray(data.employer_profiles)
    ? data.employer_profiles[0]?.company_name
    : (data.employer_profiles as { company_name?: string } | null)?.company_name;
  return {
    id: data.id,
    title: data.position_name || 'Offre',
    company: company || '',
    city: data.establishment_city || '',
    status: data.status,
  };
}

export async function fetchConversationMessages(conversationId: string): Promise<ThreadMessage[]> {
  const { data, error } = await supabase
    .from('messages')
    .select('id, sender_id, body, created_at')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true })
    .limit(200);
  if (error) {
    logger.error('fetch_report_thread', 'reportService', { conversationId, error: error.message });
    throw error;
  }
  return (data || []).map((m: any) => ({
    id: m.id,
    senderId: m.sender_id,
    body: m.body,
    createdAt: new Date(m.created_at).toLocaleString('fr-FR'),
  }));
}

async function notifyReporterOnce(
  report: ReportRow,
  status: 'resolved' | 'dismissed',
): Promise<void> {
  const actions = await fetchReportActions(report.id);
  if (actions.some((a) => a.action === 'notify_reporter')) return;

  const title = status === 'resolved'
    ? 'Votre signalement a été traité'
    : 'Votre signalement a été classé';
  const body = status === 'resolved'
    ? 'Merci, notre équipe a examiné votre signalement et a pris les mesures nécessaires.'
    : 'Merci, notre équipe a examiné votre signalement. Aucune suite n’a été donnée.';

  await sendAdminNotification(report.reporter.userId, title, body);
  await insertReportAction(report.id, 'notify_reporter', { status });
}

export async function updateReportStatus(
  report: ReportRow,
  status: ReportStatus,
  adminNotes?: string,
): Promise<void> {
  const adminId = await currentAdminId();
  const patch: Record<string, unknown> = { status };
  if (adminNotes !== undefined) patch.admin_notes = adminNotes;
  if (status === 'reviewed' || status === 'resolved' || status === 'dismissed') {
    patch.reviewed_at = new Date().toISOString();
    patch.reviewed_by = adminId;
  }
  const { error } = await supabase.from('reports').update(patch).eq('id', report.id);
  if (error) {
    logger.error('update_report_status', 'reportService', { id: report.id, status, error: error.message });
    throw error;
  }
  await insertReportAction(report.id, 'status', { status, adminNotes });
  if (status === 'resolved' || status === 'dismissed') {
    await notifyReporterOnce(report, status);
  }
  logger.functional('update_report_status', 'reportService', { id: report.id, status });
}

export async function saveAdminNotes(reportId: string, adminNotes: string): Promise<void> {
  const { error } = await supabase.from('reports').update({ admin_notes: adminNotes }).eq('id', reportId);
  if (error) {
    logger.error('save_admin_notes', 'reportService', { reportId, error: error.message });
    throw error;
  }
}

export async function warnReported(report: ReportRow, title: string, body: string): Promise<void> {
  await sendAdminNotification(report.reported.userId, title, body);
  await insertReportAction(report.id, 'warn', { title, body });
  if (report.status === 'pending') {
    await updateReportStatus(report, 'reviewed');
  }
  logger.functional('report_warn', 'reportService', { id: report.id });
}

export async function suspendReported(report: ReportRow, durationHours?: number): Promise<void> {
  await disableAccount(report.reported.userId, true, durationHours);
  await insertReportAction(
    report.id,
    durationHours ? 'temp_suspend' : 'ban',
    { durationHours: durationHours || null },
  );
  if (report.status === 'pending') {
    await updateReportStatus(report, 'reviewed');
  }
  logger.functional('report_suspend', 'reportService', { id: report.id, durationHours });
}

export async function deleteReportedContent(report: ReportRow): Promise<void> {
  if (report.targetType === 'content' && report.targetId) {
    const preview = await fetchContentPreview(report.targetId);
    const adminId = await currentAdminId();
    if (preview) {
      await rejectVideo(report.targetId, adminId, 'Signalement — contenu retiré', preview.source);
    }
  } else if (report.targetType === 'message' && report.targetId) {
    const { error } = await supabase
      .from('messages')
      .update({ body: '[Message supprimé par la modération]' })
      .eq('id', report.targetId);
    if (error) throw error;
  } else if (report.targetType === 'offer' && report.targetId) {
    await closeOfferAsAdmin(report.targetId, 'Signalement');
  } else {
    throw new Error('Aucun contenu à supprimer pour ce type de signalement');
  }
  await insertReportAction(report.id, 'delete_content', { targetType: report.targetType, targetId: report.targetId });
  if (report.status === 'pending') {
    await updateReportStatus(report, 'reviewed');
  }
  logger.functional('report_delete_content', 'reportService', { id: report.id });
}
